import _ from 'lodash';
import { AppStateProvider, simpleEffect } from 'app/shared/app_state/AppStateProvider';
import { MainService, URL_STATE_TAG } from 'app/main/MainService';
import { everyArrayParamObj, urlParamValEq } from 'app/shared/url_and_history/url_and_history';
import { isImsId } from 'app/shared/utilities/validation';
import { ImsPanelService, PanelApi, PanelSpec } from 'app/shared/ims_panel.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import DebugService from 'app/shared/debug.service';
import { TourService } from 'app/shared/help_and_information/tours/TourService';
import { generateUUID } from 'app/shared/uuid.service';
import ImsObjectCollection from 'app/shared/collections/ims_object_collection.service';

const log = (...args) => DebugService.log('ImsItemPanelService', ...args);

const k_ACTIVE_IMS_ITEM_TOUR = 'ims_item_active_tour';

/**
 * This key contains the panelTag to ims item id mapping.
 * @type {string}
 */
const k_PANEL_TO_TAG_MAP_KEY = 'imsItemPanelToTagMap';

/**
 * The shape of the value of k_PANEL_TO_TAG_MAP_KEY
 */
interface PanelToTagMap {
    // assets
    [imsItemTypeKey: string]: {
        // imsId : uuid
        [imsId: number]: string;
    };
}

const defaultPanelConfig = Object.freeze({
    detachedWidth: 400,
    persistent: false,
    resizable: false,
    innerComponent: 'imsItemPanel',
});

interface ParamObj {
    value: number;
    extra: any;
}

interface Panel extends PanelSpec {
    api: PanelApi;
}

export class ImsItemPanelService {
    state: AppStateProvider;
    private _imsPanelService: ImsPanelService;
    private _imsItemTypeRegistry: ImsItemTypeRegistry;
    private _closingPanels = [];
    private readonly _supportedItemTypes: string[];

    constructor(
        mainService: MainService,
        imsPanelService: ImsPanelService,
        imsItemTypeRegistry: ImsItemTypeRegistry,
    ) {
        this._imsPanelService = imsPanelService;
        this._imsItemTypeRegistry = imsItemTypeRegistry;

        this._supportedItemTypes = imsItemTypeRegistry
            .getRegisteredTypes()
            .filter((type) => imsItemTypeRegistry.hasPanelDetails(type));

        log('building panel state for ims item types', this._supportedItemTypes);

        this.state = new AppStateProvider({
            // Build up a keys object with a key/value pair for each supported ims type
            keys: this._supportedItemTypes.reduce(
                (keys, imsItemType) => {
                    const stateKey = this._getStateKey(imsItemType);
                    const inUrl = imsItemTypeRegistry.getPanelDetails(imsItemType).inUrl;
                    // Set the initial value if there are open panels in the URL on start up
                    const initialValue = mainService.initialStateValue(stateKey);
                    return Object.assign(keys, {
                        [stateKey]: {
                            initialValue: initialValue,
                            defaultValue: [],
                            tags: inUrl ? [URL_STATE_TAG] : [],
                            eqCheck: urlParamValEq,
                            validator: (v) => everyArrayParamObj(v, isImsId),
                        },
                    });
                },
                {
                    [k_ACTIVE_IMS_ITEM_TOUR]: {
                        initialValue: {
                            imsItemType: null,
                            imsId: null,
                        },
                        eqCheck: (a, b) => a.imsItemType === b.imsItemType && a.imsId === b.imsId,
                    },
                    [k_PANEL_TO_TAG_MAP_KEY]: {
                        // Set the initial value if there are open panels in the URL on start up
                        initialValue: this._supportedItemTypes.reduce((keys, imsItemType) => {
                            const stateKey = this._getStateKey(imsItemType);
                            const initialImsItemValue = mainService.initialStateValue(stateKey);
                            const initialPanelMapForImsItemType = {};
                            if (Array.isArray(initialImsItemValue)) {
                                // For initial values that come from the URL, add a random panel tag.
                                // These values only matter for the current session
                                initialImsItemValue.forEach((paramObj: ParamObj) => {
                                    // value is the ims item id
                                    initialPanelMapForImsItemType[+paramObj.value] = generateUUID();
                                });
                            }
                            return Object.assign(keys, {
                                [this._getStateKey(imsItemType)]: initialPanelMapForImsItemType,
                            });
                        }, {}),
                    },
                },
            ),
            // When a user opens/closes a panel or changes tab, we don't directly mutate the panel.
            // Instead we run one of the effects below, update state & trigger onChange
            effects: {
                setActiveTab: simpleEffect((state, imsItemType, imsId, tab) => ({
                    [this._getStateKey(imsItemType)]: state[this._getStateKey(imsItemType)].map(
                        (paramObj: ParamObj) =>
                            +paramObj.value === +imsId
                                ? {
                                      value: paramObj.value,
                                      extra: tab,
                                  }
                                : paramObj,
                    ),
                })),
                setActiveTour: simpleEffect((state, imsItemType, imsId) => ({
                    [k_ACTIVE_IMS_ITEM_TOUR]: {
                        imsItemType,
                        imsId,
                    },
                })),
                open: (imsItemType, panelTag, imsId, tab) => {
                    return (state) => {
                        if (!panelTag) {
                            throw new Error('panelTag must be truthy');
                        }
                        if (!imsId) {
                            throw new Error('imsId must be truthy');
                        }
                        const stateKey = this._getStateKey(imsItemType);
                        // This cloneDeep should only happen on panel open / close and the
                        // map itself should be fairly small - size is proportional to the amount of
                        // ims item panels opened.
                        const updatedPanelToTagMap = _.cloneDeep(state[k_PANEL_TO_TAG_MAP_KEY]);
                        _.set(updatedPanelToTagMap, `${stateKey}.${imsId}`, panelTag);
                        return Object.assign({}, state, {
                            [stateKey]: state[stateKey].concat(
                                tab ? { value: imsId, extra: tab } : { value: imsId },
                            ),
                            // Update the mapping of panelTag to imsId
                            [k_PANEL_TO_TAG_MAP_KEY]: updatedPanelToTagMap,
                        });
                    };
                },
                close: (imsItemType, imsId) => {
                    return (state) => {
                        const stateKey = this._getStateKey(imsItemType);
                        // This cloneDeep should only happen on panel open / close and the
                        // map itself should be fairly small - size is proportional to the amount of
                        // ims item panels opened.
                        const updatedPanelToTagMap = _.cloneDeep(state[k_PANEL_TO_TAG_MAP_KEY]);
                        // remove the ims item id to panel tag mapping
                        _.unset(updatedPanelToTagMap, [stateKey, imsId]);
                        // remove the ims item entry from the url
                        return Object.assign({}, state, {
                            [stateKey]: state[stateKey].filter(
                                // Optional panelTag matching
                                (obj) => {
                                    return +obj.value !== +imsId;
                                },
                            ),
                            [k_PANEL_TO_TAG_MAP_KEY]: updatedPanelToTagMap,
                        });
                    };
                },
                idChange: (imsItemType: string, oldImsId: number, newImsId: number) => {
                    return (state) => {
                        const stateKey = this._getStateKey(imsItemType);
                        // Take a copy so the AppStateProvider detects the property change
                        const objectsForItemType: ParamObj[] = state[stateKey];
                        const panelTagMap = state[k_PANEL_TO_TAG_MAP_KEY];
                        const panelTag = panelTagMap[stateKey][oldImsId];
                        if (!panelTag) {
                            throw new Error(
                                `Expected to find panel tag for ${stateKey}, ${oldImsId}`,
                            );
                        }
                        const modifiedObjectsForItemType = objectsForItemType.map(
                            (obj: ParamObj) => {
                                if (+obj.value === +oldImsId) {
                                    return Object.assign({}, obj, {
                                        value: newImsId,
                                    });
                                }
                                return obj;
                            },
                        );
                        // Remove the old ID / panelTag mapping, add the new ID / panelTag mapping
                        const updatedPanelTagMap = _.set(
                            _.omit(panelTagMap, [stateKey, oldImsId]),
                            `${stateKey}.${newImsId}`,
                            panelTagMap,
                        );

                        return Object.assign({}, state, {
                            [stateKey]: modifiedObjectsForItemType,
                            [k_PANEL_TO_TAG_MAP_KEY]: updatedPanelTagMap,
                        });
                    };
                },
            },
            parent: mainService.state,
        });

        // When one of the ims item type's state is changed (from one of the effects above),
        // we check to make sure each panel that should be open is actually open and on the correct tab.
        // We also check if there are any extra panels open that need to be closed.
        this.state.onChange(
            async (state) => {
                log('new state', state);

                const promises = [];

                this._supportedItemTypes.forEach((imsItemType) => {
                    const stateKey = this._getStateKey(imsItemType);

                    const shouldBeOpen = state[stateKey].map((paramObj: ParamObj) => {
                        const imsId = +paramObj.value;
                        const panelTag = _.get(
                            state[k_PANEL_TO_TAG_MAP_KEY],
                            `${stateKey}.${imsId}`,
                        );
                        if (!panelTag) {
                            throw new Error(
                                `imsItemPanelService.onChange: Expect there to be a panel tag for ${stateKey}, ${imsId}`,
                            );
                        }
                        return {
                            panelTag,
                            imsId,
                            activeTab: paramObj.extra,
                            tourMode: ImsItemPanelService._isCurrentTour(
                                state,
                                imsItemType,
                                +paramObj.value,
                            ),
                        };
                    });

                    imsPanelService.getPanelsByGroup(stateKey).forEach((panelObj) => {
                        const thisPanelTag = panelObj.tag;
                        if (
                            !this._panelIsClosing(thisPanelTag) &&
                            !shouldBeOpen.map(({ panelTag }) => panelTag).includes(thisPanelTag)
                        ) {
                            this._markPanelAsClosing(thisPanelTag);
                            const promise = imsPanelService
                                .closePanel(panelObj.id)
                                .catch((error) => {
                                    console.error(
                                        'failed to close panel; ignoring',
                                        thisPanelTag,
                                        error,
                                    );
                                });
                            promises.push(promise);
                        }
                    });

                    shouldBeOpen.forEach(({ panelTag, imsId, activeTab, tourMode }) => {
                        log('Checking should be open for', imsItemType, panelTag, imsId);
                        const panel = this._getPanel(imsItemType, imsId);
                        const isClosing = this._panelIsClosing(panelTag);

                        if (!panel) {
                            if (!isClosing) {
                                this._openRegisteredPanel(
                                    imsItemType,
                                    panelTag,
                                    imsId,
                                    activeTab,
                                    tourMode,
                                );
                            }
                        } else {
                            const updateData: any = {};
                            if (panel.innerComponentData.imsId !== imsId) {
                                log(
                                    'setting ims id for',
                                    imsItemType,
                                    panel.innerComponentData.imsId,
                                    'to',
                                    imsId,
                                );
                                updateData.imsId = imsId;
                            }

                            if (panel.innerComponentData.activeTab !== activeTab) {
                                log(
                                    'setting active tab for',
                                    imsItemType,
                                    imsId,
                                    panelTag,
                                    'to',
                                    activeTab,
                                );
                                updateData.activeTab = activeTab;
                            }

                            if (panel.innerComponentData.tourMode !== tourMode) {
                                log(
                                    'setting tour mode for',
                                    imsItemType,
                                    imsId,
                                    panelTag,
                                    'to',
                                    tourMode,
                                );
                                updateData.tourMode = tourMode;
                            }
                            if (Object.keys(updateData).length > 0) {
                                panel.api.updateData(updateData);
                            }
                        }
                    });
                });

                // Reset the list of closing panels regardless of whether the promises resolve or reject
                if (promises.length) {
                    Promise.all(promises).finally(() => {
                        this._unMarkPanelsAsClosing();
                    });
                }
            },
            {
                keys: this._supportedItemTypes
                    .map((t) => this._getStateKey(t))
                    .concat([k_ACTIVE_IMS_ITEM_TOUR]),
                runNow: true,
            },
        );

        log('finished construction', this);
    }

    private _markPanelAsClosing(tag: string) {
        if (this._closingPanels.indexOf(tag) < 0) {
            this._closingPanels.push(tag);
        }
    }

    private _unMarkPanelsAsClosing() {
        this._closingPanels = [];
    }

    private _panelIsClosing(tag: string) {
        return this._closingPanels.indexOf(tag) > -1;
    }

    private _panelTag(imsItemType: string, imsId: number): string {
        const stateKey = this._getStateKey(imsItemType);
        const panelToTagMapKey: PanelToTagMap = this.state.getState()[k_PANEL_TO_TAG_MAP_KEY];
        return _.get<any, any>(panelToTagMapKey, `${stateKey}.${imsId}`);
    }

    private static _isCurrentTour(state, imsItemType: string, imsId: number): boolean {
        return (
            state[k_ACTIVE_IMS_ITEM_TOUR].imsItemType === imsItemType &&
            state[k_ACTIVE_IMS_ITEM_TOUR].imsId === imsId
        );
    }

    private _getPanel(imsItemType: string, imsId: number) {
        const panelTag = this._panelTag(imsItemType, imsId);
        if (!panelTag) {
            return null;
        }
        return this._imsPanelService.getPanelByTag(panelTag);
    }

    private _getStateKey(imsItemType: string) {
        return this._imsItemTypeRegistry.getPanelDetails(imsItemType).stateKey;
    }

    private _openPanel(imsItemType: string, configChanges): Panel {
        const { displayName, panelDetails } = this._imsItemTypeRegistry.getTypeDetails(imsItemType);

        const panelConfig: PanelSpec = Object.assign(
            {
                headerText: displayName,
            },
            this._imsItemTypeRegistry.hasHelpDetails(imsItemType)
                ? {
                      helpKey: imsItemType,
                  }
                : {},
            defaultPanelConfig,
            panelDetails.configOverrides,
            configChanges,
        );

        log('opening panel with config', panelConfig);
        this._imsPanelService.newPanelOrFocus('detached', panelConfig);
        return this._imsPanelService.getPanelByTag(panelConfig.tag);
    }

    private _openRegisteredPanel(
        imsItemType: string,
        panelTag: string,
        imsId: number,
        activeTab: string,
        tourMode: boolean,
    ) {
        log('opening registered panel', imsItemType, imsId, 'to tab', activeTab);
        this._openPanel(imsItemType, {
            tag: panelTag,
            onClose: () => {
                const closePanel = () => {
                    return Promise.resolve(this.state.effects.close(imsItemType, imsId, panelTag));
                };

                if (ImsItemPanelService._isCurrentTour(this.state.getState(), imsItemType, imsId)) {
                    TourService.endCurrentTour();
                }
                return closePanel();
            },
            groups: [this._getStateKey(imsItemType)],
            innerComponentData: {
                imsItemType,
                imsId,
                activeTab,
                tourMode,
            },
        });
    }

    getMostRecentlyFocusedPanel(imsItemType: string) {
        return this._imsPanelService.getMostRecentlyFocusedDetachedPanelOfGroup(
            this._getStateKey(imsItemType),
        );
    }

    async tourStarted(imsItemType: string, imsId: number) {
        const panel = this._getPanel(imsItemType, imsId);
        if (panel) {
            panel.api.focus();
        }
        return this.state.effects.setActiveTour(imsItemType, imsId);
    }

    tourFinished() {
        return this.state.effects.setActiveTour(null, null);
    }

    canOpenPanelForType(imsItemType: string): boolean {
        const details = this._imsItemTypeRegistry.getPanelDetails(imsItemType);
        return !!(details && details.stateKey);
    }

    isPanelOpen(imsItemType: string, imsId: number): boolean {
        return !!this._getPanel(imsItemType, imsId);
    }

    openOrFocus(
        imsItemType: string,
        imsId: number,
        activeTab?: string,
        panelTag?: string,
    ): unknown {
        log('attempting to open/focus', imsItemType, imsId, 'to tab', activeTab);

        let panel;
        if (panelTag) {
            panel = this._imsPanelService.getPanelByTag(panelTag);
        } else {
            panel = this._getPanel(imsItemType, imsId);
        }
        if (panel) {
            log('...panel found', panel);
            panel.api.focus();
            if (activeTab) {
                return this.state.effects.setActiveTab(imsItemType, imsId, activeTab);
            }
        } else {
            log('...no panel found');
            return this.state.effects.open(
                imsItemType,
                panelTag ? panelTag : generateUUID(),
                imsId,
                activeTab,
            );
        }
    }

    updatePanelToFitContent(panelTag: string) {
        const panel = this._imsPanelService.getPanelByTag(panelTag);
        const minWidth = 600;
        const maxWidth = window.screen.width * 0.8;
        const minHeight = 400;
        const maxHeight = window.screen.height * 0.5;

        if (panel && panel.id) {
            const panelElement = document.getElementById(panel.id);
            const panelContentElement =
                panelElement.getElementsByClassName('trigger-tab-content')[0];

            if (panelContentElement) {
                let width = minWidth;
                if (panelContentElement.scrollWidth === panelContentElement.clientWidth) {
                    width = panelElement.clientWidth;
                } else if (panelContentElement.scrollWidth > panelContentElement.clientWidth) {
                    width = Math.min(
                        Math.max(panelContentElement.scrollWidth + 40, minWidth),
                        maxWidth,
                    );
                }

                let height = minHeight;
                if (panelContentElement.scrollHeight === panelContentElement.clientHeight) {
                    height = panelElement.clientHeight;
                } else if (panelContentElement.scrollHeight > panelContentElement.clientHeight) {
                    height = Math.min(
                        Math.max(panelContentElement.scrollHeight + 120, minHeight),
                        maxHeight,
                    );
                }

                this._imsPanelService.getPanelByTag(panelTag).api.resize(width, height);
            }
        }
    }

    /**
     * Register an ims item panel for a 'new' item after it has been saved.
     * i.e, called after a successful save where the ID of a panel
     * goes from undefined to a number and we want to start tracking the
     * state of the ims item panel.
     *
     * @param {string} imsItemType
     * @param {number} imsId
     * @param activeTab
     * @param {string} panelTag - the important identifier
     * @returns {Promise<void>}
     */
    async registerImsItemPanel(
        imsItemType: string,
        imsId: number,
        activeTab: string | undefined,
        panelTag: string,
    ) {
        // Do standard registration
        const panel = this._imsPanelService.getPanelByTag(panelTag);
        if (!panel) {
            throw new Error(`Expected a panel with tag ${panelTag}`);
        }
        const isRegistered = !!this._panelTag(imsItemType, imsId);
        if (!isRegistered) {
            // register it...
            // Ensure the registered panel has the correct group.
            // This comes up if we are converting a raw / new panel into a
            // registered panel.
            panel.api.addGroup(this._getStateKey(imsItemType));
            return this.state.effects.open(imsItemType, panelTag, imsId, activeTab);
        } else {
            log(`${imsItemType} panel for id ${imsId} with tag ${panelTag} is already registered`);
        }
    }

    async close(imsItemType: string, imsId: number) {
        log('attempting to close', imsItemType, imsId);
        return this.state.effects.close(imsItemType, imsId);
    }

    setActiveTab(
        imsItemType: string,
        imsId: number,
        activeTab: string,
        tourOverride?: boolean,
    ): Promise<any> {
        // Even though the tabs themselves are not clickable during the tour, there are other place in the app
        // (e.g. the context menu) that could change item tabs during the tour if this check were not here
        if (
            !tourOverride &&
            ImsItemPanelService._isCurrentTour(this.state.getState(), imsItemType, imsId)
        ) {
            log('not changing tab as this panel is in tour mode');
        } else {
            log('attempting to set active tab of', imsItemType, imsId, 'to', activeTab);
            return this.state.effects.setActiveTab(imsItemType, imsId, activeTab);
        }
    }

    // Opens a panel for the provided imsItemType, passing it
    // a new instance of the corresponding imsObject class.
    //
    // If provided, imsObjInterceptor is applied to the newly created imsObject
    // before it is passed into the imsItemPanel component.
    async openNewPanelForType(
        imsItemType: string,
        imsObjInterceptor?: (param: unknown) => void,
    ): Promise<any> {
        const panelTag = generateUUID();
        return this._openPanel(
            imsItemType,
            Object.assign({
                tag: panelTag,
                onClose: this._getOnCloseFuncForUnregisteredPanel(imsItemType, panelTag),
                innerComponentData: Object.assign(
                    {
                        imsItemType,
                    },
                    imsObjInterceptor
                        ? {
                              imsObjInterceptor,
                          }
                        : {},
                ),
            }),
        );
    }

    // Opens a panel to be controlled by a tour, the tour ending must close this panel.
    async openTourPanelForType(imsItemType: string, imsId: number): Promise<Panel> {
        // Create a panel with all the requirements for the tour.
        const panelTag = generateUUID();
        const tourPanelApi: Panel = this._openPanel(
            imsItemType,
            Object.assign({
                tag: panelTag,
                groups: [this._getStateKey(imsItemType)],
                innerComponentData: Object.assign(
                    {
                        imsItemType,
                        imsId,
                        tourMode: true,
                    },
                    {},
                ),
            }),
        );

        tourPanelApi.api.addOnAfterCloseListener(() => {
            this.tourFinished();
            TourService.endCurrentTour();
        });

        await this.state.effects.open(imsItemType, panelTag, imsId);
        // Register the tour as started
        // These two methods are required for state registration and updating.
        // We require this object to be opened into state because modifications to state are how the tours navigate the tabs in imsItemPanels
        // HACK: The side effect of this is tour panels left open on refresh are kept in the URL and are attempted to be retrieved from API, failing of course.
        // More HACK: Make sure this comes after state.effects.open()
        // As open gets called, close gets called on the panel which calls tourFinished() and clears the
        // active tour state.
        // It's related to the way this service and ImsPanelService gets diffed in order
        // to work out which panels to keep open. Not to worry, by setting the active tour
        // after this settles, everything works
        await this.state.effects.setActiveTour(imsItemType, imsId);

        return tourPanelApi;
    }

    // Opens a panel for the provided imsItemType but does not create a new imsObject
    // Simply passes rawData through to the body component
    openRawPanelForType(imsItemType: string, rawData?) {
        const panelTag = generateUUID();
        this._openPanel(
            imsItemType,
            Object.assign({
                tag: panelTag,
                innerComponentData: Object.assign(
                    {
                        imsItemType,
                        isRawPanel: true,
                    },
                    rawData
                        ? {
                              rawData,
                          }
                        : {},
                ),
                onClose: this._getOnCloseFuncForUnregisteredPanel(imsItemType, panelTag),
            }),
        );
    }

    /**
     * In store and forward mode it's possible for an ims id to change from
     * a negative to a positive number. Handle it!
     *
     * @param {number} oldId
     * @param {number} newId
     * @param imsType
     */
    public async idChange(oldId: number, newId: number, imsType: string): Promise<any> {
        return this.state.effects.idChange(imsType, oldId, newId);
    }

    public addCollectionToWatch(collection: ImsObjectCollection) {
        // No clean up. This service lives forever
        collection.addLocalToPersistentObjListener(this.idChange.bind(this));
    }

    /**
     * Special onClosed handler for unregistered panels. If the unregistered panel ends up registered at some
     * point, this will maintain the proper state for the imsItemPanelService
     *
     * @param {string} imsItemType
     * @param {string} panelTag
     * @returns {() => Promise<any>}
     * @private
     */
    private _getOnCloseFuncForUnregisteredPanel(
        imsItemType: string,
        panelTag: string,
    ): () => Promise<any> {
        return async () => {
            // Find the ims ID if it exists, i.e., if the new panel has been saved
            // and is now a standard ims item panel.
            const stateKey = this._getStateKey(imsItemType);
            const panelTagMapForType = this.state.getState()[k_PANEL_TO_TAG_MAP_KEY][stateKey];

            const stringImsId: string = Object.keys(panelTagMapForType).find((id: string) => {
                return panelTagMapForType[id] === panelTag;
            });
            // cast it to an integer
            const imsId: number = stringImsId !== undefined ? +stringImsId : undefined;

            if (ImsItemPanelService._isCurrentTour(this.state.getState(), imsItemType, imsId)) {
                TourService.endCurrentTour();
            }
            if (imsId !== undefined) {
                // The panel is part of the ims item panel service state.
                // Close via the app state provider.
                return this.state.effects.close(imsItemType, imsId, panelTag);
            } else {
                // The ID is undefined, i.e. we are looking at a new asset form.
                const panelObj = this._imsPanelService.getPanelByTag(panelTag);
                if (panelObj) {
                    // Close the panel directly.
                    return this._imsPanelService.closePanel(panelObj.id);
                }
            }
        };
    }
}
